import React from 'react';
import SaveIcon from '@mui/icons-material/Save';
import { Button, InputAdornment, Stack, TextField } from '@mui/material';

import { ColumnData, RowBase, RowEdits } from './priceGridTypes';
import { getCommonEditValue, getValidationErrorsByColumnKey } from './priceGridUtilities';

const multipleValuesText = 'Multiple';

// Tall enough to vertically center a small (40px) input and still fit its floating helper line below.
// Reserving this on the controls themselves means the message can float in every context without
// overflowing, so the input and buttons stay centered whether in the bar or a compact group row.
const controlsMinHeight = 68;

interface Props<TRow extends RowBase> {
  readonly editableColumns: readonly ColumnData<TRow>[];
  readonly edits: RowEdits;
  readonly rowsToEdit: readonly TRow[];
  readonly saveLabel: string;
  readonly onEditChange: (columnKey: string, value: string) => void;
  readonly onSave: () => void;
  readonly onClear: () => void;
  readonly showClearAction?: boolean;
  readonly trailingActions?: React.ReactNode;
}

export function BulkEditControls<TRow extends RowBase>({
  editableColumns,
  edits,
  rowsToEdit,
  saveLabel,
  onEditChange,
  onSave,
  onClear,
  showClearAction = true,
  trailingActions,
}: Props<TRow>) {
  const editedColumnCount = editableColumns.filter((column) => edits[column.key] != null).length;
  const validationErrorsByColumnKey = getValidationErrorsByColumnKey(editableColumns, edits);
  const hasInvalidEdits = Object.values(validationErrorsByColumnKey).some(Boolean);

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        // The helper line floats out of flow (see the field sx), so every field box is just the
        // input's height; centering then lines the fields and buttons up on a single centerline, and
        // the reserved min height below holds the floated message.
        alignItems: 'center',
        minHeight: controlsMinHeight,
        // Match the buttons to the input height so the row reads as one set of equal-height controls.
        '& .MuiButton-root': { minHeight: 40 },
        flex: '2 1 560px',
        flexWrap: 'wrap',
        justifyContent: { xs: 'flex-start', md: 'flex-end' },
        minWidth: 0,
        rowGap: 1,
      }}
    >
      {editableColumns.map((column) => {
        const commonEditValue = getCommonEditValue(column, rowsToEdit);
        const hasMultipleValues = edits[column.key] == null && commonEditValue == null;

        return (
          <TextField
            key={column.key}
            error={Boolean(validationErrorsByColumnKey[column.key])}
            helperText={validationErrorsByColumnKey[column.key] ?? ' '}
            label={column.title}
            placeholder={hasMultipleValues ? multipleValuesText : undefined}
            size="small"
            slotProps={{
              input: column.editStartAdornment
                ? { startAdornment: <InputAdornment position="start">{column.editStartAdornment}</InputAdornment> }
                : undefined,
              // A start adornment keeps the label from overlapping only when it is shrunk.
              inputLabel: hasMultipleValues || column.editStartAdornment ? { shrink: true } : undefined,
            }}
            value={edits[column.key] ?? commonEditValue ?? ''}
            onChange={(event) => onEditChange(column.key, event.target.value)}
            sx={{
              flex: column.key === 'notes' ? '2 1 240px' : '1 1 160px',
              minWidth: 0,
              // Float the helper line beneath the input (out of flow) so the field's layout height is
              // just the input and it stays vertically centered; the controls' reserved min height
              // holds the message so it appears without shifting the row or overflowing it.
              position: 'relative',
              '& .MuiFormHelperText-root': { position: 'absolute', top: '100%', mt: 0, lineHeight: 1.2 },
            }}
          />
        );
      })}
      <Button
        color="primary"
        disabled={editedColumnCount === 0 || hasInvalidEdits}
        size="small"
        startIcon={<SaveIcon />}
        variant="contained"
        sx={{ flex: { xs: '1 1 180px', md: '0 0 auto' }, whiteSpace: 'nowrap' }}
        onClick={onSave}
      >
        {saveLabel}
      </Button>
      {showClearAction && (
        <Button
          disabled={editedColumnCount === 0}
          size="small"
          sx={{ flex: { xs: '1 1 90px', md: '0 0 auto' } }}
          onClick={onClear}
        >
          Clear
        </Button>
      )}
      {trailingActions}
    </Stack>
  );
}
